import React from "react";
import { Box, Typography, Chip } from "@mui/material";
import { motion } from "framer-motion";

interface SelectableItem {
  text?: string;
  name?: string;
  value?: string;
  [key: string]: any;
}

interface SelectableChipListProps {
  items: (string | SelectableItem)[];
  primaryColor: string;
  onItemSelect: (itemText: string, itemId?: string) => void;
  label?: string;
  icon?: React.ReactNode;
  extractId?: (item: string | SelectableItem) => string | undefined;
  /** Highlight chips already chosen (multi-Rx strength picker). */
  selectedItems?: string[];
  /**
   * Optional visual label for the chip. The selection value sent to ``onItemSelect``
   * always remains ``getItemText`` (the plain name) — this only changes what is shown.
   */
  getItemLabel?: (item: string | SelectableItem) => React.ReactNode;
  /**
   * True for a list rendered on a past (non-latest) conversation turn: the choice has
   * already been made and acted on, so clicking any chip here — including one that
   * looks unselected — must not re-fire that turn's action against the current state.
   * Chips still render (so the history stays readable) and the one in ``selectedItems``
   * still highlights; they're just inert.
   */
  disabled?: boolean;
}

const SelectableChipList: React.FC<SelectableChipListProps> = ({
  items,
  primaryColor,
  onItemSelect,
  label,
  icon,
  extractId,
  selectedItems = [],
  getItemLabel,
  disabled = false,
}) => {
  const selectedSet = new Set(
    (selectedItems || []).map((s) => String(s).trim().toLowerCase()).filter(Boolean)
  );
  // Extract text from item (handle both string and object formats)
  const getItemText = (item: string | SelectableItem): string => {
    if (typeof item === 'string') {
      return item;
    }
    return item?.text || item?.name || item?.value || item?.pharmacyName || item?.pharmacy_name || item?.nameConcatenatedWithAddress || String(item);
  };

  // Extract ID if available
  const getItemId = (item: string | SelectableItem): string | undefined => {
    if (typeof item === 'string') {
      return undefined;
    }
    if (extractId) {
      return extractId(item);
    }
    return item?.id || item?.pharmacyId;
  };

  // Handle item click
  const handleItemClick = (item: string | SelectableItem) => {
    if (disabled) return;
    const itemText = getItemText(item);
    const itemId = getItemId(item);
    if (itemText) {
      onItemSelect(itemText, itemId);
    }
  };

  // Don't render if no items
  if (!items || items.length === 0) {
    return null;
  }

  // Shared chip styles. When `disabled` (a past turn's already-acted-on list), drop the
  // hover/active affordances entirely rather than relying only on MUI's built-in disabled
  // opacity -- otherwise an inert chip still visually invites a click on hover.
  const chipStyles = {
    backgroundColor: `${primaryColor}15`,
    color: primaryColor,
    border: `1px solid ${primaryColor}40`,
    cursor: disabled ? "default" : "pointer",
    transition: "all 0.2s ease",
    ...(disabled
      ? {}
      : {
          "&:hover": {
            backgroundColor: `${primaryColor}25`,
            border: `1px solid ${primaryColor}60`,
            transform: "translateY(-1px)",
            boxShadow: `0 2px 4px ${primaryColor}20`,
          },
          "&:active": {
            transform: "translateY(0)",
          },
        }),
    ...(icon && {
      "& .MuiChip-icon": {
        color: primaryColor,
      },
    }),
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <Box
        sx={{
          width: "100%",
          mt: 2,
          mb: 2,
          display: "flex",
          flexDirection: "column",
          gap: 1,
        }}
      >
        {label && (
          <Typography
            variant="body2"
            sx={{ 
              fontWeight: 600,
              mb: 0.5,
            }}
          >
            {label}
          </Typography>
        )}
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 1,
          }}
        >
          {items.map((item, index) => {
            const itemText = getItemText(item);
            const isSelected = selectedSet.has(itemText.trim().toLowerCase());
            const chipLabel = getItemLabel ? getItemLabel(item) : itemText;
            const isRichLabel = getItemLabel && typeof chipLabel !== "string";

            return (
              <Chip
                key={index}
                {...(icon && { icon: icon as React.ReactElement })}
                label={chipLabel}
                onClick={disabled ? undefined : () => handleItemClick(item)}
                disabled={disabled && !isSelected}
                sx={{
                  ...chipStyles,
                  ...(isRichLabel && {
                    height: "auto",
                    py: 0.75,
                    "& .MuiChip-label": {
                      display: "block",
                      whiteSpace: "normal",
                    },
                  }),
                  ...(isSelected && {
                    backgroundColor: `${primaryColor}35`,
                    border: `2px solid ${primaryColor}`,
                    fontWeight: 600,
                    // Keep the highlighted/chosen chip fully legible even when the whole
                    // list is disabled (a past turn) -- MUI's default disabled opacity
                    // would otherwise wash out the one chip that matters most: the answer.
                    ...(disabled && { opacity: 1 }),
                  }),
                }}
              />
            );
          })}
        </Box>
      </Box>
    </motion.div>
  );
};

export default SelectableChipList;

